// details page for jobs and housing
import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { db } from "../firebase/config";
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import Map from "../components/Map";

const DetailPage = ({ userName }) => {
  const navigate = useNavigate();
  const { type, id } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [details, setDetails] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [userData, setUserData] = useState(null);

  const isJob = type === "jobs";

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        const docRef = doc(db, "listings", id);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();

          // Kullanıcı verilerini al
          const userRef = collection(db, "users");
          const q = query(userRef, where("uid", "==", data.userId));
          const userSnap = await getDocs(q);

          if (!userSnap.empty) {
            const userData = userSnap.docs[0].data();
            setUserData(userData);
          }

          setDetails({
            ...data,
            createdAt: data.createdAt?.toDate() || new Date(),
          });
        } else {
          setError("Listing not found");
        }
      } catch (err) {
        console.error("Error fetching details:", err);
        setError("Error loading listing details");
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [id]);

  // Resim seçme fonksiyonu
  const handleImageClick = (image, index) => {
    setSelectedImage(image);
    setCurrentImageIndex(index);
  };

  if (loading) {
    return (
      <div className='min-h-screen bg-[#F8F9FA] py-8'>
        <div className='max-w-4xl mx-auto px-4'>
          <div className='flex justify-center items-center h-64'>
            <div className='animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500'></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !details) {
    return (
      <div className='min-h-screen bg-[#F8F9FA] py-8'>
        <div className='max-w-4xl mx-auto px-4'>
          <div className='text-center text-gray-600 font-architects-daughter'>
            {error || "Listing not found"}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-[#F8F9FA] py-8'>
      <div className='max-w-4xl mx-auto px-4'>
        <div className='bg-white rounded-lg shadow-lg p-6'>
          {/* Company Name at the top */}
          <div className='mb-8 border-b pb-6'>
            <h1 className='text-3xl font-bold text-[#212529] font-architects-daughter mb-2'>
              {(
                details.companyName ||
                userData?.companyName ||
                "Company Name Not Available"
              ).toUpperCase()}
            </h1>
            {/* İlan başlığı */}
            <p className='text-xl text-gray-600 font-architects-daughter'>
              {details.title}
            </p>
          </div>

          {/* Housing Images */}
          {!isJob && details.images && details.images.length > 0 && (
            <div className='mb-8'>
              <h3 className='text-xl font-bold mb-4 font-architects-daughter'>
                Photos
              </h3>
              <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4'>
                {details.images.map((image, index) => (
                  <img
                    key={index}
                    src={image}
                    alt={`Housing ${index + 1}`}
                    className='w-full h-48 object-cover rounded-lg cursor-pointer hover:opacity-90'
                    onClick={() => handleImageClick(image, index)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Details Section */}
          <div className='space-y-6'>
            {/* Job Title and Location for Jobs */}
            {isJob && (
              <>
                <DetailRow label='Position' value={details.title} />
              </>
            )}

            {/* Location for Housing */}

            <DetailRow
              label='Posted On'
              value={details.createdAt.toLocaleDateString()}
            />

            {/* İlan Detayları */}
            <div className='space-y-4 mt-4'>
              {isJob ? (
                // İş ilanı detayları
                <>
                  <div className='flex items-center space-x-2'>
                    <span className='font-medium'>Hourly Payment:</span>
                    <span>${details.salary}/hr</span>
                  </div>
                  <div className='flex items-center space-x-2'>
                    <span className='font-medium'>Tips:</span>
                    <span>{details.tipsIncluded ? "Yes" : "No"}</span>
                  </div>
                  <div className='flex items-center space-x-2'>
                    <span className='font-medium'>Transportation:</span>
                    <span>{details.transportationProvided ? "Yes" : "No"}</span>
                  </div>
                  <div className='flex items-center space-x-2'>
                    <span className='font-medium'>Overtime Available:</span>
                    <span>{details.overtimeAvailable ? "Yes" : "No"}</span>
                  </div>
                  <div className='flex items-center space-x-2'>
                    <span className='font-medium'>Housing Provided:</span>
                    <span>{details.housingProvided ? "Yes" : "No"}</span>
                  </div>
                  <div className='flex items-center space-x-2'>
                    <span className='font-medium'>English Level:</span>
                    <span className='capitalize'>{details.englishLevel}</span>
                  </div>
                </>
              ) : (
                // Ev ilanı detayları
                <>
                  <div className='flex items-center space-x-2'>
                    <span className='font-medium'>Room Type:</span>
                    <span className='capitalize'>{details.roomType}</span>
                  </div>
                  <div className='flex items-center space-x-2'>
                    <span className='font-medium'>Furnished:</span>
                    <span>{details.furnished ? "Yes" : "No"}</span>
                  </div>
                  <div className='flex items-center space-x-2'>
                    <span className='font-medium'>Internet Included:</span>
                    <span>{details.internetIncluded ? "Yes" : "No"}</span>
                  </div>
                  <div className='flex items-center space-x-2'>
                    <span className='font-medium'>Available From:</span>
                    <span>{details.availableFrom}</span>
                  </div>
                  <div className='flex items-center space-x-2'>
                    <span className='font-medium'>Available Until:</span>
                    <span>{details.availableUntil}</span>
                  </div>
                </>
              )}
            </div>

            {/* Description */}
            <div className='pt-6 border-t'>
              <h3 className='text-xl font-bold mb-4 font-architects-daughter'>
                Description
              </h3>
              <p className='text-[#495057] font-architects-daughter whitespace-pre-wrap'>
                {details.description}
              </p>
            </div>

            {/* Location and Map Section */}
            <div className='pt-6 border-t'>
              <h3 className='text-xl font-bold mb-4 font-architects-daughter'>
                Location
              </h3>
              <DetailRow label='Address' value={details.location} />
              <div className='mt-4'>
                <Map address={details.location} />
              </div>
            </div>

            {/* İletişim Bilgileri */}
            <div className='mt-6 space-y-4'>
              <div className='flex items-center space-x-2'>
                <svg
                  className='w-5 h-5 text-gray-600'
                  fill='currentColor'
                  viewBox='0 0 24 24'>
                  <path d='M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z' />
                </svg>
                <span className='text-gray-700'>
                  WhatsApp Available: {details.whatsappAvailable ? "Yes" : "No"}
                </span>
              </div>

              {/* Contact Information */}
              <div className='mt-8'>
                <h3 className='text-xl font-semibold mb-4'>
                  Contact Information
                </h3>
                <div className='space-y-3'>
                  <p>
                    <span className='font-medium'>Contact Person:</span>{" "}
                    {details.contactName} {details.contactSurname}
                  </p>
                  <p>
                    <span className='font-medium'>Phone:</span>{" "}
                    <a
                      href={`tel:${details.contactPhone}`}
                      className='text-blue-600 hover:underline'>
                      {details.contactPhone}
                    </a>
                    {details.whatsappAvailable && (
                      <span className='ml-2 text-green-600'>
                        (WhatsApp Available)
                      </span>
                    )}
                  </p>
                  <p>
                    <span className='font-medium'>Email:</span>{" "}
                    <a
                      href={`mailto:${details.contactEmail}`}
                      className='text-blue-600 hover:underline'>
                      {details.contactEmail}
                    </a>
                  </p>
                  {details.facebookUsername && (
                    <p>
                      <span className='font-medium'>Facebook:</span>{" "}
                      <a
                        href={`https://facebook.com/${details.facebookUsername}`}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='text-blue-600 hover:underline'>
                        {details.facebookUsername}
                      </a>
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Navigation Buttons */}
          <div className='mt-8 flex flex-col sm:flex-row gap-4'>
            <button
              onClick={() => navigate(isJob ? "/jobs" : "/housing")}
              className='flex-1 py-4 text-xl font-architects-daughter border-2 border-blue-600 rounded hover:bg-blue-100 transition'>
              Back to {isJob ? "Jobs" : "Housing"}
            </button>
          </div>
        </div>
      </div>

      {/* Image Modal */}
      {selectedImage && (
        <div
          className='fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4'
          onClick={() => setSelectedImage(null)}>
          <div className='relative max-w-4xl w-full'>
            {/* Close Button */}
            <button
              className='absolute top-4 right-4 text-white text-xl font-bold bg-black bg-opacity-50 w-10 h-10 rounded-full hover:bg-opacity-75'
              onClick={() => setSelectedImage(null)}>
              ×
            </button>

            {/* Navigation Arrows */}
            {details.images && details.images.length > 1 && (
              <>
                <button
                  className='absolute left-4 top-1/2 -translate-y-1/2 text-white text-3xl font-bold bg-black bg-opacity-50 w-12 h-12 rounded-full hover:bg-opacity-75 flex items-center justify-center'
                  onClick={(e) => {
                    e.stopPropagation();
                    const newIndex =
                      (currentImageIndex - 1 + details.images.length) %
                      details.images.length;
                    setCurrentImageIndex(newIndex);
                    setSelectedImage(details.images[newIndex]);
                  }}>
                  ‹
                </button>
                <button
                  className='absolute right-4 top-1/2 -translate-y-1/2 text-white text-3xl font-bold bg-black bg-opacity-50 w-12 h-12 rounded-full hover:bg-opacity-75 flex items-center justify-center'
                  onClick={(e) => {
                    e.stopPropagation();
                    const newIndex =
                      (currentImageIndex + 1) % details.images.length;
                    setCurrentImageIndex(newIndex);
                    setSelectedImage(details.images[newIndex]);
                  }}>
                  ›
                </button>
              </>
            )}

            {/* Main Image */}
            <img
              src={selectedImage}
              alt='Enlarged view'
              className='w-full h-auto max-h-[90vh] object-contain'
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const DetailRow = ({ label, value }) => (
  <div className='flex items-start'>
    <span className='font-medium w-40 text-[#212529] font-architects-daughter'>
      {label}:
    </span>
    <span className='text-[#495057] font-architects-daughter'>{value}</span>
  </div>
);

export default DetailPage;
